import { unzipSync, zipSync } from "fflate";
import { Archive, ArchiveCompression, ArchiveFormat, type ArchiveEntryFile } from "libarchive.js";
import { errorMessage } from "./format";

// The Emscripten worker resolves `libarchive.wasm` relative to its own URL,
// so it is served as a static asset at /vendor (see dev.ts and build.ts).
Archive.init({
  getWorker: () => new Worker(new URL("/vendor/worker-bundle.js", import.meta.url), { type: "module" })
});

export type TrainingDocument = {
  name: string;
  path: string;
  size: number;
  file: File;
};

const TEXT_EXTENSIONS =
  /\.(txt|md|markdown|csv|json|jsonl|html|xml|js|ts|tsx|jsx|py|rs|go|java|c|cpp|h|hpp|css|scss|sql|yaml|yml|toml|ini|log)$/i;

const ARCHIVE_EXTENSIONS = /\.(zip|7z)$/i;

export function looksTextBased(file: File | { name: string; type?: string }) {
  return file.type?.startsWith("text/") || file.type === "application/json" || TEXT_EXTENSIONS.test(file.name);
}

export function looksArchiveBased(file: File) {
  return ARCHIVE_EXTENSIONS.test(file.name);
}

export async function loadTrainingDocuments(files: FileList | File[]) {
  const rejected: string[] = [];
  const groups = await Promise.all(
    Array.from(files, async (file) => {
      if (looksArchiveBased(file)) {
        try {
          return await readArchive(file);
        } catch (error) {
          rejected.push(`${file.name}: ${errorMessage(error)}`);
          return [];
        }
      }
      if (looksTextBased(file)) {
        return [toTrainingDocument(file, file.name)];
      }
      rejected.push(file.name);
      return [];
    })
  );
  return { documents: groups.flat(), rejected };
}

export async function documentsToCorpus(documents: TrainingDocument[]) {
  const chunks = await Array.fromAsync(documents, (document) => document.file.text());
  return chunks.join("\n\n");
}

export async function createTrainingDataZip(documents: TrainingDocument[]) {
  const entries = Object.fromEntries(
    await Promise.all(
      documents.map(async (document) => [
        safeArchivePath(document.path),
        new Uint8Array(await document.file.arrayBuffer())
      ] as const)
    )
  );
  return new Blob([zipSync(entries, { level: 6 })], { type: "application/zip" });
}

export async function createTrainingData7z(documents: TrainingDocument[]) {
  const archiveFile = await Archive.write({
    files: documents.map((document) => ({
      // libarchive.js types `file` as ArchiveEntryFile instead of File.
      file: document.file as unknown as ArchiveEntryFile,
      pathname: safeArchivePath(document.path)
    })),
    outputFileName: "enkeltrain-training-data.7z",
    compression: ArchiveCompression.LZMA,
    format: ArchiveFormat.SEVEN_ZIP,
    passphrase: null
  });
  return new Blob([archiveFile], { type: "application/x-7z-compressed" });
}

async function readArchive(file: File) {
  return /\.zip$/i.test(file.name) ? readZip(file) : readLibarchive(file);
}

async function readZip(file: File) {
  const entries = unzipSync(new Uint8Array(await file.arrayBuffer()));
  const documents: TrainingDocument[] = [];
  for (const [path, bytes] of Object.entries(entries)) {
    if (path.endsWith("/") || !looksTextBased({ name: path })) {
      continue;
    }
    const safePath = safeArchivePath(path);
    documents.push(
      toTrainingDocument(
        new File([bytes], safePath, { type: guessMimeType(safePath), lastModified: file.lastModified }),
        safePath
      )
    );
  }
  return documents;
}

async function readLibarchive(file: File) {
  const archive = await Archive.open(file);
  const extracted = await archive.extractFiles();
  const documents: TrainingDocument[] = [];
  collectExtractedFiles(extracted, "", documents);
  return documents;
}

function collectExtractedFiles(value: unknown, path: string, documents: TrainingDocument[]) {
  if (value instanceof File) {
    const filePath = safeArchivePath(path || value.name);
    if (looksTextBased({ name: filePath, type: value.type })) {
      documents.push(toTrainingDocument(value, filePath));
    }
    return;
  }

  if (!value || typeof value !== "object") {
    return;
  }

  for (const [name, child] of Object.entries(value)) {
    collectExtractedFiles(child, path ? `${path}/${name}` : name, documents);
  }
}

function toTrainingDocument(file: File, path: string): TrainingDocument {
  const safePath = safeArchivePath(path);
  return {
    name: safePath.split("/").pop() || safePath,
    path: safePath,
    size: file.size,
    file
  };
}

function safeArchivePath(path: string) {
  const cleaned = path
    .replaceAll("\\", "/")
    .split("/")
    .filter((part) => part && part !== "." && part !== "..")
    .join("/");
  return cleaned || "training-data.txt";
}

function guessMimeType(path: string) {
  if (/\.(json|jsonl)$/i.test(path)) {
    return "application/json";
  }
  if (/\.(html|xml)$/i.test(path)) {
    return "text/html";
  }
  return "text/plain";
}
